import { AddIcon, ChevronRightIcon, DeleteIcon } from '@sketchnote/icons';
import {
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  MoreActionMenu,
} from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import ProgressBar from 'react-bootstrap/ProgressBar';
import TextareaAutosize from 'react-textarea-autosize';
import { Node } from 'slate';
import { useBoardStore } from 'src/features/boards';

import { useTasksStore } from '../store/useTasksStore';
import { Task } from '../types/tasks';

export const EpicTasks = ({ items, task }: { task: any; items: any & { isLoading?: boolean } }) => {
  const [isNewTask, setIsNewTask] = useState(false);
  const [newTask, setNewTask] = useState('');
  const onAddSubTask = useTasksStore((s) => s.onAddSubTask);
  const onDeleteSubTask = useTasksStore((s) => s.onDeleteSubTask);
  const onMarkAsCompleteSubTask = useTasksStore((s) => s.onMarkAsCompleteSubTask);
  const isFullScreen = useTasksStore((s) => s.isFullScreen);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const boardsStore = useBoardStore();
  const onToggleNewTask =
    (enable = true) =>
    () => {
      setIsNewTask(enable);
    };

  return (
    <div>
      <div className="mb-24">
        {items?.map((subTask) => (
          <Link
            key={subTask._id}
            href={
              //public
              boardsStore.isPublicView
                ? `/${mode}/${orgId}/files/${task.fileId}/public?view=${router.query.view}&type=${router.query.type}&taskId=${subTask._id}`
                : isFullScreen
                ? `/${mode}/${orgId}/tasks/${subTask._id}`
                : `/${mode}/${orgId}/files/${task.fileId}?view=${task.board?.id}&type=${router.query.type}&taskId=${subTask._id}`
            }
          >
            <div className="p-12 shadow-mdl4 rounded-sm flex items-center justify-between mb-14 cursor-pointer hover:shadow-mdl5 transition-shadow duration-300">
              <div>
                <div id="left" className="flex items-center">
                  <p>{getFormattedValue(subTask.name)}</p>
                </div>
                <div className="flex items-center text-gray-500">
                  {subTask.assignee?.length ? (
                    <div className="flex">
                      <AvatarGroup maxCount={1}>
                        {subTask.assignee.map((obj) => (
                          <Avatar
                            name={cn(obj.firstname, obj.lastname)}
                            src={obj.avatar}
                            type="photo"
                            key={obj.memberId}
                            wrapperClassName="-mr-8 border-2 border-white"
                          />
                        ))}
                      </AvatarGroup>
                      {subTask.assignee.length > 2 && (
                        <Avatar
                          name={subTask.assignee.length - 2}
                          tooltip={false}
                          type={'initials'}
                          src={''}
                          wrapperClassName="-ml-10 border-white border-1 transform transition duration-500 hover:scale-125"
                        />
                      )}
                    </div>
                  ) : null}
                  {subTask.assignDate?.length ? (
                    <span className={cn('ml-8 mt-2 text-xss text-gray-500 flex', {})}>
                      <span className="leading-22">{formatDateLight(subTask.assignDate)}</span>
                    </span>
                  ) : null}
                </div>
              </div>
              <div>
                <ChevronRightIcon />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

function getFormattedValue(value): string {
  if (typeof value === 'string') {
    return value;
  } else if (Array.isArray(value)) {
    return value.map((obj) => Node.string(obj)).join('\n');
  }

  return '';
}

const formatDateLight = (dates) => {
  const dateFormat = 'MMM d, yyyy';
  if (dates.length === 2 && dates?.[0] && dates?.[1]) {
    return `${format(parseISO(dates[0]), dateFormat)} - ${format(parseISO(dates[1]), dateFormat)}`;
  } else if (dates.length === 1 && dates?.[0]) {
    return format(parseISO(dates[0]), dateFormat);
  }
  return '';
};

export const calculatePercentage = (total: number, value: number) =>
  parseFloat(((value / total) * 100).toFixed());
